import { useState } from 'react';
import { type CareerProfile } from 'CareerProfiles';
import { getProgramConfigValue } from 'Program';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { getAvatarColor } from '../../utils/avatarColor';
import { ProfileSections } from './ProfileSections';
import { ProgramBadges } from './ProgramBadges';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';
import 'StudentNetwork/locales/student_network/field_options-en.json';

type ProfileDetailProps = {
    profile: CareerProfile;
    onBack?: () => void;
    onMessage?: () => void;
};

type EduExperience = {
    degreeProgram?: boolean;
    degree?: string;
    major?: string;
    educationalOrganization?: { text?: string };
    graduationYear?: number;
};

type ProfileDetailPresentationProps = {
    profileId: string;
    displayName: string;
    companyRole: string;
    location: string;
    bio: string;
    programBadges: { branding: string; label: string }[];
    educationLines: { institution: string; detail: string; date?: string }[];
    workLines: { organization: string; role: string }[];
    interests: string[];
    lookingFor: string[];
    avatarBg: string;
    avatarUrl: string | null;
    initials: string;
    showImg: boolean;
    isAnonymized: boolean;
    onImgError: () => void;
    onBack?: () => void;
    onMessage?: () => void;
};

// oxlint-disable-next-line max-lines-per-function, complexity -- view component with mobile/desktop layouts
export function ProfileDetailPresentation({
    profileId,
    displayName,
    companyRole,
    location,
    bio,
    programBadges,
    educationLines,
    workLines,
    interests,
    lookingFor,
    avatarBg,
    avatarUrl,
    initials,
    showImg,
    isAnonymized,
    onImgError,
    onBack,
    onMessage,
}: ProfileDetailPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.profileDetail' });

    const avatarElement = (
        <div
            className="flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: avatarBg }}
        >
            {showImg ? (
                <img src={avatarUrl!} alt="" className="h-full w-full object-cover" onError={onImgError} />
            ) : (
                <span className="text-[48px] font-bold text-white">{initials}</span>
            )}
        </div>
    );

    return (
        <div data-career-profile-id={profileId}>
            {/* Mobile layout — full-width scrollable profile */}
            <div className="flex flex-1 flex-col bg-white px-5 md:hidden">
                {/* Avatar with back chevron + message button overlapping */}
                <div className="relative flex justify-center pb-4 pt-5">
                    {/* Back chevron — near top of avatar */}
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="absolute start-0 top-7 flex items-center text-primary transition-opacity hover:opacity-70"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                        </button>
                    )}
                    {/* Avatar */}
                    {avatarElement}
                    {/* Message button — near top of avatar */}
                    {!isAnonymized && onMessage && (
                        <button
                            type="button"
                            className="absolute end-0 top-7 h-9 rounded-full bg-black px-4 text-base font-normal text-white hover:bg-black/80"
                            onClick={onMessage}
                        >
                            {t('message')}
                        </button>
                    )}
                </div>

                {/* Name */}
                <div className="text-xl font-bold text-black">{displayName}</div>

                {/* Title, Company */}
                {companyRole && <div className="text-base text-black">{companyRole}</div>}

                {/* Location */}
                {location && <div className="text-base text-slate-grey">{location}</div>}

                {/* Program badges */}
                <ProgramBadges badges={programBadges} className="mt-1" />

                {/* Bio */}
                {bio && (
                    <div className="mt-4">
                        <span className="text-base text-black">{bio}</span>
                    </div>
                )}

                {/* Divider */}
                <div className="my-6 h-px bg-slate-grey-100" />

                {/* Sections — full width on mobile */}
                <ProfileSections
                    bio=""
                    interests={interests}
                    lookingFor={lookingFor}
                    educationLines={educationLines}
                    workLines={workLines}
                />
            </div>

            {/* Desktop layout — matches Sketch: avatar right, info left, full-width divider */}
            <div className="hidden px-[30px] py-[30px] md:block">
                {onBack && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="mb-4 flex items-center gap-2 text-[20px] text-primary transition-opacity hover:opacity-70"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        {t('backToList')}
                    </button>
                )}

                <div className="flex gap-6">
                    {/* Left column — text info, takes remaining space */}
                    <div className="min-w-0 flex-1">
                        {/* Name — 25px bold */}
                        <div className="text-[25px] font-bold leading-tight text-black">{displayName}</div>

                        {/* Company/role — 20px */}
                        {companyRole && <div className="mt-1 text-[20px] text-black">{companyRole}</div>}

                        {/* Location — 16px slate-grey */}
                        {location && <div className="text-base text-slate-grey">{location}</div>}

                        {/* Program badges */}
                        <ProgramBadges badges={programBadges} className="mt-1" />
                    </div>

                    {/* Right column — avatar, fixed size */}
                    <div
                        className="flex h-[120px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-full"
                        style={{ backgroundColor: avatarBg }}
                    >
                        {showImg ? (
                            <img src={avatarUrl!} alt="" className="h-full w-full object-cover" onError={onImgError} />
                        ) : (
                            <span className="text-[48px] font-bold text-white">{initials}</span>
                        )}
                    </div>
                </div>

                {/* Bio + Message button row */}
                {(bio || (!isAnonymized && onMessage)) && (
                    <div className="mt-4 flex items-start gap-4">
                        {bio && <div className="flex-1 text-lg text-black">{bio}</div>}
                        {!isAnonymized && onMessage && (
                            <button
                                type="button"
                                className="me-[13px] ms-auto h-9 shrink-0 rounded-full border border-slate-grey-200 bg-white px-4 text-base font-normal text-black hover:bg-slate-grey-100-alt"
                                onClick={onMessage}
                            >
                                {t('message')}
                            </button>
                        )}
                    </div>
                )}

                {/* Full-width divider */}
                <div className="my-5 h-px bg-slate-grey-100" />

                <ProfileSections
                    bio=""
                    interests={interests}
                    lookingFor={lookingFor}
                    educationLines={educationLines}
                    workLines={workLines}
                />
            </div>
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- data derivation wrapper
export function ProfileDetail({ profile, onBack, onMessage }: ProfileDetailProps) {
    const { t: tDegree } = useTranslation('back_royal', {
        keyPrefix: 'careers.edit_career_profile.education_experience_detail',
    });
    const { t: tFieldOptions } = useTranslation('back_royal', {
        keyPrefix: 'student_network.field_options',
    });

    const isAnonymized =
        (profile as Record<string, unknown>).anonymized === true || profile.prefStudentNetworkPrivacy === 'anonymous';
    const displayName = isAnonymized ? (profile.nickname ?? '?') : (profile.name ?? '');
    const jobTitle = profile.workExperiences?.[0]?.jobTitle ?? '';
    const company =
        profile.workExperiences?.[0]?.professionalOrganization?.text ??
        profile.workExperiences?.[0]?.professional_organization?.text ??
        '';
    const companyRole = [jobTitle, company].filter(Boolean).join(', ');
    const location = [profile.city, profile.state, profile.country].filter(Boolean).join(', ');
    const bio = profile.personalFact ?? '';

    const programBadges = (profile.programs ?? [])
        .map(p => ({
            branding: p.institutionBranding,
            label: getProgramConfigValue(p.programType, 'shortProgramTitle') as string,
        }))
        .filter(b => !!b.label);

    const rawEdu = (profile as Record<string, unknown>).educationExperiences as EduExperience[] | undefined;
    const educationLines = (rawEdu ?? [])
        .filter(e => e.educationalOrganization?.text)
        .map(e => ({
            institution: e.educationalOrganization?.text ?? '',
            detail: [tDegree(e.degree ?? '', { defaultValue: e.degree ?? '' }), e.major].filter(Boolean).join(' '),
            date: e.graduationYear ? String(e.graduationYear) : undefined,
        }));

    const workLines = (profile.workExperiences ?? []).map(w => ({
        organization: w.professionalOrganization?.text ?? w.professional_organization?.text ?? '',
        role: w.jobTitle ?? '',
    }));

    const rawInterests = ((profile as Record<string, unknown>).studentNetworkInterests as unknown[] | undefined) ?? [];
    const interests = rawInterests
        .map(i => {
            const key = typeof i === 'string' ? i : ((i as Record<string, unknown>)?.text as string);
            if (!key) return '';
            return tFieldOptions(key, { defaultValue: key });
        })
        .filter(Boolean);

    const rawLookingFor =
        ((profile as Record<string, unknown>).studentNetworkLookingFor as unknown[] | undefined) ?? [];
    const lookingFor = rawLookingFor
        .map(i => {
            const key = typeof i === 'string' ? i : ((i as Record<string, unknown>)?.text as string);
            if (!key) return '';
            return tFieldOptions(key, { defaultValue: key });
        })
        .filter(Boolean);

    const initials = (displayName || '?')
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    const avatarBg = getAvatarColor(profile.id);
    const [imgFailed, setImgFailed] = useState(false);
    const showImg = !!profile.avatarUrl && !isAnonymized && !imgFailed;

    return (
        <ProfileDetailPresentation
            profileId={profile.id}
            displayName={displayName}
            companyRole={companyRole}
            location={location}
            bio={bio}
            programBadges={programBadges}
            educationLines={educationLines}
            workLines={workLines}
            interests={interests}
            lookingFor={lookingFor}
            avatarBg={avatarBg}
            avatarUrl={profile.avatarUrl}
            initials={initials}
            showImg={showImg}
            isAnonymized={isAnonymized}
            onImgError={() => setImgFailed(true)}
            onBack={onBack}
            onMessage={onMessage}
        />
    );
}
